/**
 * What a Routed away row says about the item beyond the hand-off (UX round
 * 2026-10-13, the status-sweep explorer): who sent it (Jess or Merlin's
 * nightly router), what the person last answered and when, and that it was
 * followed up since it was sent. Without them the list said "No reply yet"
 * on QT-10056 while the item showed Tim's answer, never said which items
 * Jess had chased that morning, and mixed her routings with the router's.
 *
 * Read from what the door already loads: the thread's `route_origin` and
 * the door's asks on the item (asksForThread). Pure logic (repo
 * convention: logic in .ts, components in .tsx).
 */
import type { DoorThreadRead } from '../../services/qcpInvestigations';
import type { DoorDataRequestRead } from '../../services/qcpDataRequests';
import { askedOn } from './answersView';
import { sameName } from './deliveryView';
import { originActor } from './routeOriginView';
import { shortExcerpt } from './handOffView';

export interface RoutedAnswer {
  who: string;
  at: string;
  question: string;
  /** The first words of the answer; '' when the answer carried no text. */
  excerpt: string;
  /** It came in before the item was sent to them this time. */
  beforeSend: boolean;
}

export interface RoutedFollowUp {
  /** Who was asked since the send, oldest ask first, each once. */
  names: string[];
  /** The newest of those asks (ISO). */
  at: string;
}

function ms(iso: string | null | undefined): number {
  const t = iso ? new Date(iso).getTime() : NaN;
  return Number.isNaN(t) ? NaN : t;
}

/** The newest answer on any of the item's asks; null when none. */
export function latestAnswer(
  thread: Pick<DoorThreadRead, 'routed_at'>,
  asks: ReadonlyArray<DoorDataRequestRead>,
): RoutedAnswer | null {
  let best: RoutedAnswer | null = null;
  for (const r of asks) {
    if (r.status !== 'answered' && r.status !== 'closed') continue;
    const last = [...(r.responses ?? [])].sort((a, b) => ms(b.created_at) - ms(a.created_at))[0];
    const at = last?.created_at ?? r.answered_at;
    if (!at || Number.isNaN(ms(at))) continue;
    if (best && ms(best.at) >= ms(at)) continue;
    const who =
      (r.audience === 'team' || r.audience === 'company') && last?.responder_identifier
        ? last.responder_identifier
        : r.target_name;
    const sent = ms(thread.routed_at);
    best = {
      who,
      at,
      question: r.question_title,
      excerpt: shortExcerpt(last?.body_text ?? '', 120),
      beforeSend: !Number.isNaN(sent) && ms(at) < sent,
    };
  }
  return best;
}

/** Asks that went out on the item after it was sent on; null when none. */
export function followUpsSinceSend(
  thread: Pick<DoorThreadRead, 'routed_at'>,
  asks: ReadonlyArray<DoorDataRequestRead>,
): RoutedFollowUp | null {
  const sent = ms(thread.routed_at);
  if (Number.isNaN(sent)) return null;
  const after = asks
    .filter((r) => r.status !== 'proposed' && r.status !== 'cancelled' && ms(r.sent_at) > sent)
    .sort((a, b) => ms(a.sent_at) - ms(b.sent_at));
  if (after.length === 0) return null;
  const names: string[] = [];
  for (const r of after) if (!names.includes(r.target_name)) names.push(r.target_name);
  return { names, at: after[after.length - 1].sent_at as string };
}

function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** "Followed up today: Tim Gebhardt" / "Followed up Oct 12: Emily Adams and Laura Caswell". */
export function followUpWords(f: RoutedFollowUp, now: Date = new Date()): string {
  const d = new Date(f.at);
  const when = sameDay(d, now) ? 'today' : askedOn(f.at);
  return `Followed up ${when}: ${joinNames(f.names)}`;
}

/** "Tim Gebhardt answered Oct 8, before it was sent to them: “…”". */
export function answerWords(a: RoutedAnswer): string {
  const when = askedOn(a.at);
  const head = `${a.who} answered ${when}${a.beforeSend ? ', before it was sent to them' : ''}`;
  return a.excerpt ? `${head}: “${a.excerpt}”` : `${head}: “${a.question}”`;
}

/** "Sent by you" / "Sent by Merlin's nightly router" (+ " on Oct 6"); null
 *  without a record of who routed it. */
export function sentByWords(
  thread: Partial<Pick<DoorThreadRead, 'route_origin'>>,
  userName: string | null | undefined,
): string | null {
  const origin = thread.route_origin;
  if (!origin) return null;
  const mine = origin.actor_kind === 'human' && !!userName && sameName(origin.actor_label, userName);
  const who = mine ? 'you' : originActor(origin);
  const day = origin.at ? askedOn(origin.at) : '';
  return `Sent by ${who}${day ? ` on ${day}` : ''}`;
}

/**
 * The "No reply" chip's words, or null when there is a reply to show
 * instead: a reply to the hand-off, or an answer after the send.
 */
export function noReplyWords(hasHandOffReply: boolean, answer: RoutedAnswer | null): string | null {
  if (hasHandOffReply) return null;
  if (answer && !answer.beforeSend) return null;
  return answer ? 'No reply since it was sent' : 'No reply yet';
}
